import { Download } from "@payping/icons";
import { Spin, Tooltip, Typography, Upload } from "@payping/web-components";
import type { FormInstance } from "@payping/web-components/legacyComponents/Form/types";
import type { RcFile, UploadChangeParam } from "@payping/web-components/components/upload/types";
import classNames from "classnames";
import React, { useState } from "react";

import { useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { excelDownloadId, uploadDraggerId } from "@/assets/googleTrackIds";
import styles from "../../../../assets/scss/Settlement/Settlement.module.scss";
import { SettlementConfiguration } from "..";

interface Props {
  requestFunction: (file: string | Blob | RcFile) => Promise<string>;
  uploadLoading: boolean;
  form: FormInstance<SettlementConfiguration>;
}

const BulkRequest: React.FC<Props> = (props) => {
  const { form, requestFunction, uploadLoading } = props;
  const { t } = useAppTranslation();
  const [loadingUpload, setLoadingUpload] = useState<boolean>(false);
  const { overview } = useAppSelector((state) => state.cashOuts);

  const onChange = async (info: UploadChangeParam) => {
    const { status } = info.file;
    if (status === "uploading") {
      setLoadingUpload(true);
    } else if (status === "done") {
      setLoadingUpload(false);
    } else setLoadingUpload(false);
  };

  const callApi = async (options: any) => {
    const { file, onSuccess, onError } = options;
    requestFunction(file)
      .then(async (result) => {
        onSuccess?.(JSON.parse(result));
      })
      .catch(onError);
    return {
      abort() {},
    };
  };

  return (
    <div>
      <Tooltip
        className="tw:align-middle"
        placement="top"
        title={form.getFieldValue("title") ? "" : t("settlement.nameRequired")}
      >
        <Spin size="large" spinning={loadingUpload} tip={t("settlement.loadingExcel")}>
          <Upload.Dragger
            disabled={uploadLoading || !form.getFieldValue("title")}
            onChange={onChange}
            customRequest={callApi}
            allowedExtensions={["xls", "xlsx"]}
            showUploadList={false}
            maxCount={1}
            className={classNames(styles.draggerUpload, "tw:mb-4!")}
            data-google-track={uploadDraggerId}
            // @ts-ignore: TODO: add onDrop type to components
            onDrop={() => {
              window.dataLayer.push({ event: "Upload Dropped" });
            }}
          >
            {!loadingUpload && (
              <>
                <Typography.Title level={5}>{t("settlement.create.noneItem")}</Typography.Title>
                <Typography>{t("settlement.create.dragOrClickHere")}</Typography>
                <Typography.Link
                  onClick={(event) => event.stopPropagation()}
                  href={overview?.excelURL}
                  data-google-track={excelDownloadId}
                >
                  <Download width={22} /> {t("settlement.create.sampleLink")}
                </Typography.Link>
              </>
            )}
          </Upload.Dragger>
        </Spin>
      </Tooltip>
    </div>
  );
};

export default BulkRequest;
